import Anthropic from '@anthropic-ai/sdk';
import { resolveBridgeUrl } from '../../shared/bridge-resolver';
import { normalizeFigure, type Figure } from './figure';

export interface ExtractedItem {
  subject: 'math' | 'chinese' | 'english' | 'nature' | 'social';
  kp: string;
  kind: 'mcq' | 'fill';
  prompt: string;
  options?: (string | number)[];
  correctAnswer: number | string;
  explanation: string;
  /** 小孩手寫的作答(自動批改用;空字串 = 空白 / 讀不到)。 */
  studentAnswer?: string;
  /** 自動判對錯:true=答對、false=答錯、null/undefined=讀不到無法判,交家長看。 */
  isCorrect?: boolean | null;
  /**
   * 這題依賴的圖表(統計表 / 長條圖 / 象形圖 / 時鐘)的**結構化資料**。App 端用
   * `renderFigure` 重新生成乾淨的圖(不裁照片);無圖則 undefined。boss 2026-06-10。
   */
  figure?: Figure;
}

/** Read lazily so tests (and Vite) can inject VITE_WORKER_URL at call time. */
function workerUrl(): string | undefined {
  const v = import.meta.env.VITE_WORKER_URL as string | undefined;
  return v ? v : undefined;
}

/**
 * How long to wait for the bridge OCR call before falling through to the worker.
 * Bridge availability is already confirmed by `resolveBridgeUrl` (a fast
 * /bridge/url lookup); this timeout covers the actual Claude vision extraction,
 * which shells out to `claude -p` and routinely takes 10-40s. 2.5s aborted every
 * real OCR → "都用 Claude" never actually used the bridge (boss 2026-06-08)。
 * 2026-06-11:批改 prompt 變紮實後單塊 ~70-90s、bridge 端上限 180s → 前端要
 * 比 bridge 多留緩衝,否則前端先放棄、bridge 白算。
 */
const BRIDGE_TIMEOUT_MS = 200_000;

const KP_HINTS = [
  'math.mul.2x1', 'math.div.basic', 'math.add.carry',
  'chinese.zhuyin.basic', 'chinese.segment.basic',
  'english.alphabet.phonics',
  'nature.water-states',
  'social.community.basic',
];

/**
 * Extract questions from a photographed exam paper.
 *
 * Primary path: POST the image to the Worker `/ocr` proxy so the Claude key
 * stays on the backend. Falls back to the legacy browser-side `@anthropic-ai/sdk`
 * call (VITE_ANTHROPIC_API_KEY) when VITE_WORKER_URL is unset or the worker is
 * unavailable (5xx/503). Signature and return type are unchanged.
 */
export async function extractFromBrowserFile(file: File): Promise<ExtractedItem[]> {
  // 送 OCR 前先套 EXIF 轉正(手機相片常側躺,顯示正但像素側躺 → OCR 漏抓)。
  const { base64, mediaType } = await fileToUprightBase64(file);

  // Provider order: local Claude bridge (first) → worker (next) → browser key (last).
  const bridge = await resolveBridgeUrl();
  if (bridge) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), BRIDGE_TIMEOUT_MS);
    try {
      const res = await fetch(`${bridge}/ocr`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64, mediaType }),
        signal: controller.signal,
      });
      if (res.ok) {
        return (await res.json()) as ExtractedItem[];
      }
      // Bridge reachable but failed → fall through to worker/browser fallback.
    } catch {
      // Bridge unreachable/timed out (not running) → fall through to worker/browser.
    } finally {
      clearTimeout(timer);
    }
  }

  const url = workerUrl();
  if (url) {
    try {
      const res = await fetch(`${url}/ocr`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64, mediaType }),
      });
      if (res.ok) {
        return (await res.json()) as ExtractedItem[];
      }
      // 4xx (client error, not 503) → real failure, surface it.
      // 5xx / 503 (AI not configured) → graceful fallback to browser path.
      if (res.status < 500) {
        throw new Error(`ocr failed: ${res.status}`);
      }
    } catch (e) {
      // 4xx: surface. Network error or 5xx: fall through to browser fallback.
      if (e instanceof Error && e.message.startsWith('ocr failed:')) throw e;
    }
  }

  return extractViaBrowser(base64, mediaType);
}

/** One vocab pair extracted from a vocab-list photo. */
export interface ExtractedVocab {
  word: string;
  zh: string;
}

/**
 * Extract English-word / Chinese-meaning pairs from a photographed vocab list.
 *
 * Same provider order as {@link extractFromBrowserFile} (local Claude bridge →
 * Worker `/ocr`) but with `mode:'vocab'`. Tolerant by design: any failure
 * (no provider reachable, non-ok status, bad JSON) resolves to `[]` so the
 * batch-import UI can keep going across multiple photos. Never throws.
 */
export async function extractVocabFromImage(file: File): Promise<ExtractedVocab[]> {
  let base64: string;
  let mediaType: AllowedMedia;
  try {
    ({ base64, mediaType } = await fileToUprightBase64(file)); // EXIF 轉正
  } catch {
    return [];
  }

  const payload = JSON.stringify({ imageBase64: base64, mediaType, mode: 'vocab' });

  // Provider order: local Claude bridge (first) → worker (next). No browser-SDK
  // fallback here — vocab import is best-effort and must not throw.
  const bridge = await resolveBridgeUrl();
  if (bridge) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), BRIDGE_TIMEOUT_MS);
    try {
      const res = await fetch(`${bridge}/ocr`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: payload,
        signal: controller.signal,
      });
      if (res.ok) return normalizeVocab(await res.json());
      // Bridge reachable but failed → fall through to the worker.
    } catch {
      // Bridge unreachable/timed out → fall through to the worker.
    } finally {
      clearTimeout(timer);
    }
  }

  const url = workerUrl();
  if (url) {
    try {
      const res = await fetch(`${url}/ocr`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: payload,
      });
      if (res.ok) return normalizeVocab(await res.json());
    } catch {
      // Network error → give up gracefully.
    }
  }

  return [];
}

/** Result of {@link extractAuto}: the classified type + its extracted items. */
export interface AutoExtractResult {
  type: 'exam' | 'vocab';
  exam?: ExtractedItem[];
  vocab?: ExtractedVocab[];
}

/**
 * Auto-classify a photo (考卷 vs 單字表) and extract accordingly.
 *
 * Same provider order as {@link extractFromBrowserFile} (local Claude bridge →
 * Worker `/ocr`) but with `mode:'auto'`; the backend returns a `{ type, items }`
 * object. Tolerant by design: any failure (no provider, non-ok status, bad
 * shape) resolves to `{ type:'exam', exam:[] }` so the unified upload UI can keep
 * going across many photos. Never throws.
 */
export async function extractAuto(file: File): Promise<AutoExtractResult> {
  // EXIF 轉正 → 切 **2×2 重疊格子**(各塊只剩 ~¼ 內容、密度低,讀得齊;涵蓋左右
  // 兩頁跨頁 + 單頁過長)→ **平行送**(bridge 每請求各自 spawn claude、可同時跑 →
  // 牆鐘 ≈ 一塊;boss 2026-06-10「辨識好久」+「只抓八題」)。合併用文字去重(不打第三個
  // 「語意合併」呼叫 —— 那步才是拖慢 + 出錯回 0 的元兇,boss 2026-06-09「直接爛掉/不要
  // 提 API,用 CLI」)。**所有塊都失敗 → 整張再讀一次保底,絕不回 0。** 殘留近似重複留給
  // 家長確認畫面手動丟棄;漏的用「➕ 補一題」。
  let tiles: string[];
  let fullB64 = '';
  try {
    const canvas = await fileToUprightCanvas(file);
    if (!canvas) {
      fullB64 = (await fileToUprightBase64(file)).base64;
      tiles = [fullB64];
    } else {
      fullB64 = canvas.toDataURL('image/jpeg', 0.92).split(',')[1] ?? '';
      const W = canvas.width;
      const H = canvas.height;
      // 夠大的相片切 **2×2 重疊格子**(各邊 12% 重疊)。一張照片可能是「左右兩頁
      // 跨頁」(該左右切)也可能是「單頁過長」(該上下切),2×2 兩種都涵蓋;每塊只剩
      // ~¼ 內容、密度低,模型讀得齊。平行送 → 牆鐘 ≈ 一塊(boss 2026-06-10「只抓到
      // 八題」=之前上下切把左右兩頁各砍一半、太密)。
      if (W >= 800 && H >= 800) {
        const ox = Math.round(W * 0.12);
        const oy = Math.round(H * 0.12);
        const midX = Math.round(W / 2);
        const midY = Math.round(H / 2);
        const rects: [number, number, number, number][] = [
          [0, 0, midX + ox, midY + oy],
          [midX - ox, 0, W - (midX - ox), midY + oy],
          [0, midY - oy, midX + ox, H - (midY - oy)],
          [midX - ox, midY - oy, W - (midX - ox), H - (midY - oy)],
        ];
        tiles = rects
          .map(([x, y, w, h]) => canvasRectToBase64(canvas, x, y, w, h))
          .filter((b) => b.length > 0);
      } else {
        tiles = [fullB64].filter((b) => b.length > 0);
      }
      if (tiles.length === 0) tiles = fullB64 ? [fullB64] : [(await fileToUprightBase64(file)).base64];
    }
  } catch {
    return { type: 'exam', exam: [] };
  }

  // **平行送切塊,但限流 2 路 + 失敗重試一輪**(2026-06-11 教訓:4 塊全平行會把
  // 單機 bridge 的部分請求拖過逾時 → 整個半邊大題消失且不重試)。2 路下牆鐘仍
  // ≈ 2 塊的時間;偶發逾時/壞回應的塊再補打一次,真不行才跳過。
  const TILE_CONCURRENCY = 2;
  const results: (unknown | null)[] = new Array(tiles.length).fill(null);
  async function ocrPass(idxs: number[]): Promise<void> {
    let cursor = 0;
    async function lane(): Promise<void> {
      while (cursor < idxs.length) {
        const i = idxs[cursor++];
        results[i] = await postOcrRaw(tiles[i], 'auto');
      }
    }
    await Promise.all(Array.from({ length: Math.min(TILE_CONCURRENCY, idxs.length) }, () => lane()));
  }
  await ocrPass(tiles.map((_, i) => i));
  const failedTiles = results.map((r, i) => (r === null ? i : -1)).filter((i) => i >= 0);
  // 全失敗交給下面既有的整張保底;部分失敗 → 失敗塊重試一輪。
  if (failedTiles.length > 0 && failedTiles.length < tiles.length) await ocrPass(failedTiles);
  const parsed: AutoExtractResult[] = [];
  for (const r of results) {
    if (r !== null) parsed.push(normalizeAuto(r));
  }

  // 過半數塊判為單字表 → 單字;否則考卷(文字去重)。
  const vocabVotes = parsed.filter((r) => r.type === 'vocab').length;
  if (parsed.length > 0 && vocabVotes > parsed.length / 2) {
    return { type: 'vocab', vocab: normalizeVocab(parsed.flatMap((r) => r.vocab ?? [])) };
  }
  let exam = dedupeExamItems(parsed.flatMap((r) => r.exam ?? []));

  // 保底:切了塊但 0 題(各塊失敗/各自空)→ 整張再讀一次,絕不回 0。
  if (exam.length === 0 && tiles.length > 1 && fullB64) {
    const r = await postOcrRaw(fullB64, 'auto');
    if (r !== null) {
      const p = normalizeAuto(r);
      if (p.type === 'vocab') return { type: 'vocab', vocab: normalizeVocab(p.vocab ?? []) };
      exam = dedupeExamItems(p.exam ?? []);
    }
  }
  return { type: 'exam', exam };
}

/** Coerce an unknown auto-mode `{type,items}` response into a typed result. */
function normalizeAuto(raw: unknown): AutoExtractResult {
  if (!raw || typeof raw !== 'object') return { type: 'exam', exam: [] };
  const type = (raw as { type?: unknown }).type === 'vocab' ? 'vocab' : 'exam';
  const items = (raw as { items?: unknown }).items;
  const arr = Array.isArray(items) ? items : [];
  if (type === 'vocab') return { type: 'vocab', vocab: normalizeVocab(arr) };
  return { type: 'exam', exam: arr.map(normalizeExamItem).filter((x): x is ExtractedItem => x !== null) };
}

/**
 * 收斂單一考卷題:沒題幹或沒正解的殘片直接丟(曾在家長確認頁長出空白卡、
 * 正解 undefined — 2026-06-11);`figure` 欄位驗證成合法 Figure(壞的丟掉),
 * 其餘欄位原樣帶過。讓家長確認 / 錯題本能拿到「可重繪的圖表資料」。
 */
function normalizeExamItem(raw: unknown): ExtractedItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const it = raw as ExtractedItem & { figure?: unknown };
  if (typeof it.prompt !== 'string' || it.prompt.trim().length < 4) return null; // 空殼/表格殘片(如 prompt="18")
  if (it.correctAnswer === undefined || it.correctAnswer === null) return null;
  const fig = normalizeFigure(it.figure);
  if (fig) it.figure = fig;
  else delete it.figure;
  return it;
}

/** Coerce an unknown OCR response into clean `{word,zh}` pairs (drops junk). */
function normalizeVocab(raw: unknown): ExtractedVocab[] {
  if (!Array.isArray(raw)) return [];
  const out: ExtractedVocab[] = [];
  for (const r of raw) {
    if (r && typeof r === 'object') {
      const word = (r as { word?: unknown }).word;
      const zh = (r as { zh?: unknown }).zh;
      if (typeof word === 'string' && word.trim().length > 0) {
        out.push({ word: word.trim(), zh: typeof zh === 'string' ? zh.trim() : '' });
      }
    }
  }
  return out;
}

/** Legacy browser-side path: dev-only, uses VITE_ANTHROPIC_API_KEY directly. */
async function extractViaBrowser(
  base64: string,
  mediaType: 'image/png' | 'image/jpeg' | 'image/webp',
): Promise<ExtractedItem[]> {
  const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
  if (!apiKey) throw new Error('VITE_ANTHROPIC_API_KEY not configured. Add it to .env (dev-only).');

  const client = new Anthropic({ apiKey, dangerouslyAllowBrowser: true });

  const resp = await client.messages.create({
    model: 'claude-opus-4-5',
    max_tokens: 4096,
    messages: [{
      role: 'user',
      content: [
        { type: 'image', source: { type: 'base64', media_type: mediaType, data: base64 } },
        { type: 'text', text: buildPrompt() },
      ],
    }],
  });

  const text = resp.content
    .filter(b => b.type === 'text')
    .map(b => (b as { type: 'text'; text: string }).text)
    .join('');
  const trimmed = text.replace(/^```(?:json)?\n?|\n?```$/g, '').trim();
  return JSON.parse(trimmed) as ExtractedItem[];
}

function buildPrompt(): string {
  return `Extract every multi-choice or fill-in question from this 3rd-grade exam paper. Output a JSON array of objects with these fields:
- subject (math|chinese|english|nature|social)
- kp (closest match from: ${KP_HINTS.join(', ')})
- kind ("mcq" or "fill")
- prompt (the question text)
- options (mcq only, 2-4 items)
- correctAnswer (mcq: 0-based index; fill: the expected string)
- explanation (1-sentence)

Output ONLY a valid JSON array. No markdown. No commentary.`;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Result is "data:image/png;base64,iVBOR..."; strip the prefix
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Bytes shape `fileToUprightBase64` returns (already-upright pixels). */
type AllowedMedia = 'image/png' | 'image/jpeg' | 'image/webp';

/**
 * Parse the EXIF Orientation tag (0x0112, value 1..8) from a JPEG buffer.
 * Returns 1 (no rotation) when absent / not a JPEG / unparseable.
 *
 * 為什麼重要(boss 2026-06-09「我給你是正的」):手機拍的相片像素其實常是側躺,
 * 靠 EXIF Orientation 標記讓「顯示」變正。OCR 直接讀原始位元組看到的是側躺的圖
 * → 嚴重漏抓(實測:側躺 3 題,轉正後同一張 12 題)。送 OCR 前必須先套用此標記。
 */
export function parseExifOrientation(buffer: ArrayBuffer): number {
  try {
    const view = new DataView(buffer);
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return 1; // not JPEG
    let offset = 2;
    while (offset + 4 < view.byteLength) {
      const marker = view.getUint16(offset);
      if ((marker & 0xff00) !== 0xff00) break;
      if (marker === 0xffe1) {
        // APP1; check "Exif\0\0" then parse the TIFF header.
        if (view.getUint32(offset + 4) !== 0x45786966) return 1;
        const tiff = offset + 10;
        const little = view.getUint16(tiff) === 0x4949;
        const g16 = (o: number): number => view.getUint16(o, little);
        const g32 = (o: number): number => view.getUint32(o, little);
        const ifd0 = tiff + g32(tiff + 4);
        const count = g16(ifd0);
        for (let i = 0; i < count; i++) {
          const entry = ifd0 + 2 + i * 12;
          if (g16(entry) === 0x0112) return g16(entry + 8) || 1;
        }
        return 1;
      }
      offset += 2 + view.getUint16(offset + 2);
    }
  } catch {
    /* ignore — fall through to no-rotation */
  }
  return 1;
}

/** True only when the runtime has a real 2D canvas (browser) — not jsdom/SSR. */
function hasCanvas2d(): boolean {
  try {
    if (typeof document === 'undefined') return false;
    const c = document.createElement('canvas');
    return typeof c.getContext === 'function' && !!c.getContext('2d');
  } catch {
    return false;
  }
}

/** A decoded image + whether EXIF orientation was already applied by the loader. */
interface Drawable {
  img: ImageBitmap | HTMLImageElement;
  /** true → 像素已正(瀏覽器套過 EXIF),呼叫端不要再手動轉。 */
  exifApplied: boolean;
}

/** Reject `p` after 12s so a stuck decode degrades gracefully (no hang). */
function withDecodeTimeout<T>(p: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('image decode timeout')), 12_000);
  });
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer!));
}

/**
 * Load a File into a drawable image.
 *   - Modern browsers: `createImageBitmap(file, { imageOrientation: 'from-image' })`
 *     已正確套用 EXIF 旋轉 → `exifApplied: true`(呼叫端**不要再手動轉**,否則雙重
 *     旋轉把圖弄歪;boss 2026-06-09 上傳只抓到 1 題就是這個雙轉 bug)。
 *   - 無 createImageBitmap 的舊環境:`<img>` → `exifApplied: false`,由呼叫端依
 *     parseExifOrientation 手動轉。
 * createImageBitmap 失敗時**不**退回 `<img>`(避免測試/壞圖 hang);直接 throw。
 */
async function loadDrawable(file: File): Promise<Drawable> {
  if (typeof createImageBitmap === 'function') {
    const bmp = await withDecodeTimeout(
      createImageBitmap(file, { imageOrientation: 'from-image' }),
    );
    return { img: bmp, exifApplied: true };
  }
  const img = await withDecodeTimeout(
    new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image decode error'));
      el.src = URL.createObjectURL(file);
    }),
  );
  return { img, exifApplied: false };
}

/**
 * Read a photo → **apply its EXIF orientation** (rotate pixels upright) → return
 * upright JPEG base64. Falls back to the original bytes when already upright,
 * when there is no canvas (tests / SSR), or on any error — so it never breaks
 * the existing flow, only improves rotated phone photos.
 */
async function fileToUprightBase64(file: File): Promise<{ base64: string; mediaType: AllowedMedia }> {
  const passthrough = async (): Promise<{ base64: string; mediaType: AllowedMedia }> => ({
    base64: await fileToBase64(file),
    mediaType: (file.type && ['image/png', 'image/jpeg', 'image/webp'].includes(file.type)
      ? file.type
      : 'image/jpeg') as AllowedMedia,
  });
  try {
    if (!hasCanvas2d()) return passthrough();
    const orientation = parseExifOrientation(await file.arrayBuffer());
    // 已正且不需切塊 → passthrough,省一次 re-encode(也讓無真影像解碼的測試環境
    // 走原樣 base64,不卡 loadDrawable)。
    if (orientation === 1) return passthrough();
    const canvas = await fileToUprightCanvas(file);
    if (!canvas) return passthrough();
    const base64 = canvas.toDataURL('image/jpeg', 0.92).split(',')[1];
    return base64 ? { base64, mediaType: 'image/jpeg' } : passthrough();
  } catch {
    return passthrough();
  }
}

/** Rotate a File upright (EXIF) onto a canvas. null when no canvas / on error. */
async function fileToUprightCanvas(file: File): Promise<HTMLCanvasElement | null> {
  try {
    if (!hasCanvas2d()) return null;
    const { img, exifApplied } = await loadDrawable(file);
    // 瀏覽器已套 EXIF(createImageBitmap from-image)→ orientation 視為 1(不再手動轉,
    // 避免雙重旋轉);否則(舊環境 <img>)依 EXIF 手動轉。
    const orientation = exifApplied ? 1 : parseExifOrientation(await file.arrayBuffer());
    const w = (img as { width: number }).width;
    const h = (img as { height: number }).height;
    const swap = orientation >= 5;
    const canvas = document.createElement('canvas');
    canvas.width = swap ? h : w;
    canvas.height = swap ? w : h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    switch (orientation) {
      case 2: ctx.transform(-1, 0, 0, 1, w, 0); break;
      case 3: ctx.transform(-1, 0, 0, -1, w, h); break;
      case 4: ctx.transform(1, 0, 0, -1, 0, h); break;
      case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
      case 6: ctx.transform(0, 1, -1, 0, h, 0); break;
      case 7: ctx.transform(0, -1, -1, 0, h, w); break;
      case 8: ctx.transform(0, -1, 1, 0, 0, w); break;
      default: break;
    }
    ctx.drawImage(img as CanvasImageSource, 0, 0);
    return canvas;
  } catch {
    return null;
  }
}

/** A rectangle [sx,sy,sw,sh] of a canvas as JPEG base64 ('' on failure). */
function canvasRectToBase64(
  canvas: HTMLCanvasElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
): string {
  try {
    if (sw <= 0 || sh <= 0) return '';
    const t = document.createElement('canvas');
    t.width = sw;
    t.height = sh;
    const ctx = t.getContext('2d');
    if (!ctx) return '';
    ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
    return t.toDataURL('image/jpeg', 0.92).split(',')[1] ?? '';
  } catch {
    return '';
  }
}

/**
 * One OCR call through the provider chain (local Claude bridge → Worker). Returns
 * the parsed JSON (any shape) or null. Used by the tiled `extractAuto` so each
 * page-slice is a separate request.
 */
async function postOcrRaw(base64: string, mode: 'auto' | 'exam' | 'vocab'): Promise<unknown | null> {
  if (!base64) return null;
  const payload = JSON.stringify({ imageBase64: base64, mediaType: 'image/jpeg', mode });
  const bridge = await resolveBridgeUrl();
  if (bridge) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), BRIDGE_TIMEOUT_MS);
    try {
      const res = await fetch(`${bridge}/ocr`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: payload,
        signal: controller.signal,
      });
      if (res.ok) {
        const data: unknown = await res.json();
        // bridge 心跳串流模式錯誤也回 200 + {error}(避開通道對非 2xx 長請求的
        // CORS 問題)→ 視為本塊失敗,讓呼叫端走重試/fallback。
        if (data && typeof data === 'object' && 'error' in (data as Record<string, unknown>)) {
          return null;
        }
        return data;
      }
    } catch {
      /* fall through to worker */
    } finally {
      clearTimeout(timer);
    }
  }
  const url = workerUrl();
  if (url) {
    try {
      const res = await fetch(`${url}/ocr`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: payload,
      });
      if (res.ok) return await res.json();
    } catch {
      /* give up */
    }
  }
  return null;
}

/** Normalise a prompt to a dedup key (空白[含全形]/標點/填答格全去,只留實字)。 */
function promptKey(prompt: unknown): string {
  return String(prompt ?? '')
    .replace(/[\s　]+/g, '')
    .replace(/[，。．,.;；:：、（）()【】[\]「」？?！!_＿]/g, '')
    .slice(0, 60);
}

/** 從 claude 回傳的文字裡解析「要保留的題目編號」(1-based)→ 0-based 去重索引。 */
export function parseKeepIndices(text: string, n: number): number[] {
  const nums = (text.match(/\d+/g) ?? [])
    .map((s) => parseInt(s, 10) - 1)
    .filter((i) => Number.isInteger(i) && i >= 0 && i < n);
  return [...new Set(nums)];
}

/**
 * 語意去重:讓 claude 自己合併「同一題被多次 OCR 成稍不同字」的重複(文字/模糊比對
 * 做不到 —— 例如「源泉→水里」vs「水里→濁水」字很像卻是不同題)。bridge /complete
 * 不可用 / 失敗 / 回傳怪異 → 退回 `dedupeExamItems`(crude 文字去重),絕不阻斷。
 * boss 2026-06-09「opencv 不能用就找別的」。
 */
export async function mergeDuplicatesSemantically(items: ExtractedItem[]): Promise<ExtractedItem[]> {
  if (items.length <= 1) return items;
  const bridge = await resolveBridgeUrl();
  if (!bridge) return dedupeExamItems(items);

  const list = items
    .map((it, i) => `${i + 1}. ${String(it.prompt ?? '').replace(/\s+/g, ' ').slice(0, 80)}`)
    .join('\n');
  const prompt =
    '以下是同一張考卷頁、多次辨識出來的題目,有重複(同一題被用稍微不同的字轉錄)。' +
    '請挑出「不重複的題目」,每一組重複只留一個。注意:不同火車路線、不同學校、不同人名' +
    '是不同題,不要誤併。只輸出『要保留的題目編號』,用逗號分隔,不要任何解說。\n\n' +
    list;

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), BRIDGE_TIMEOUT_MS);
      const res = await fetch(`${bridge}/complete`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ prompt }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!res.ok) continue;
      const data = (await res.json()) as { text?: string };
      const keep = parseKeepIndices(data.text ?? '', items.length);
      if (keep.length >= 1) return keep.map((i) => items[i]).filter(Boolean);
    } catch {
      /* retry once */
    }
  }
  return dedupeExamItems(items);
}

/**
 * Merge exam items from multiple tiles, dropping near-identical prompts. 去重採
 * **前綴包含**:同一題在兩塊重疊區被各轉一次、長度不同(一塊只看到前半)→ 其一
 * 是另一的前綴 → 視為同題,以較長(資訊多)的為主,**欄位合併**:表格常落在
 * 某一切塊、編號完整的題幹在另一切塊,整顆換掉會把 figure / 學生作答丟掉,
 * 子題就沒了表格資料、批改瞎判(2026-06-11 高鐵時刻表卷教訓)。
 */
export function dedupeExamItems(items: ExtractedItem[]): ExtractedItem[] {
  const kept: { it: ExtractedItem; key: string }[] = [];
  for (const it of items) {
    const key = promptKey(it?.prompt);
    if (!key) { kept.push({ it, key }); continue; }
    const dupIdx = kept.findIndex((k) => k.key && (k.key.startsWith(key) || key.startsWith(k.key)));
    if (dupIdx >= 0) {
      const prev = kept[dupIdx];
      const longerIsNew = key.length > prev.key.length;
      const base = longerIsNew ? it : prev.it;
      const other = longerIsNew ? prev.it : it;
      if (!base.figure && other.figure) base.figure = other.figure;
      if ((!base.studentAnswer || base.studentAnswer.trim() === '') && other.studentAnswer) {
        base.studentAnswer = other.studentAnswer;
        if (base.isCorrect == null && other.isCorrect != null) base.isCorrect = other.isCorrect;
      }
      kept[dupIdx] = { it: base, key: longerIsNew ? key : prev.key };
      continue;
    }
    kept.push({ it, key });
  }
  return kept.map((k) => k.it);
}
